import { Controller } from "@hotwired/stimulus"

// Drives the "How Fern works" scroll demo: a tall track with a sticky stage.
// Page scroll progress reveals server-rendered chat beats one by one, advances
// the six-step guide rail, and runs the micro-animations (budget slider,
// before/after wipe, book fan). Beats are rendered in the ERB partial; this
// controller only orchestrates their visibility. The rAF loop runs only while
// the track is near the viewport.
export default class extends Controller {
  static targets = [
    "track", "scrollbox", "thread", "typing",
    "fill", "node", "num", "title", "desc", "cta", "restart"
  ]

  connect() {
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.reduceMotion = this.motionQuery.matches
    this.onMotionChange = (event) => { this.reduceMotion = event.matches }
    this.motionQuery.addEventListener("change", this.onMotionChange)

    this.beats = Array.from(this.threadTarget.querySelectorAll("[data-beat]"))
    this.budgetFill = this.threadTarget.querySelector("[data-budget-fill]")
    this.budgetKnob = this.threadTarget.querySelector("[data-budget-knob]")
    this.render = this.threadTarget.querySelector("[data-render]")
    this.afterWrap = this.render?.querySelector("[data-afterwrap]")
    this.wipeLine = this.render?.querySelector("[data-wipeline]")
    this.book = this.threadTarget.querySelector("[data-book]")
    this.budgetIndex = this.beats.findIndex((el) => el.contains(this.budgetFill))
    this.renderIndex = this.beats.findIndex((el) => el.contains(this.render))
    this.bookIndex = this.beats.findIndex((el) => el.contains(this.book))

    this.displayProgress = 0
    this.targetProgress = 0
    this.currentStep = 0
    this.chatTarget = 0
    this.chatCurrent = 0
    this.swapTimer = null
    this.rafId = null
    this.budgetSet = false

    this.onScroll = () => { this.targetProgress = this.computeProgress() }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll)
    this.onScroll()
    this.displayProgress = this.targetProgress
    this.setStep(1, true)

    // Only animate while the track is near the viewport.
    this.visibility = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => entry.isIntersecting ? this.startLoop() : this.stopLoop()) },
      { rootMargin: "25% 0px" }
    )
    this.visibility.observe(this.trackTarget)
  }

  disconnect() {
    this.stopLoop()
    this.visibility?.disconnect()
    clearTimeout(this.swapTimer)
    this.motionQuery.removeEventListener("change", this.onMotionChange)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
  }

  startLoop() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => this.frame())
  }

  stopLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = null
  }

  replay() {
    const top = this.trackTarget.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: top - 2, behavior: this.reduceMotion ? "auto" : "smooth" })
  }

  computeProgress() {
    const rect = this.trackTarget.getBoundingClientRect()
    const total = rect.height - window.innerHeight
    if (total <= 0) return 0
    return Math.min(1, Math.max(0, -rect.top / total))
  }

  setStep(step, force = false) {
    if (step === this.currentStep && !force) return
    this.currentStep = step
    const node = this.nodeTargets[step - 1]
    if (!node) return

    this.numTarget.innerHTML = `${String(step).padStart(2, "0")}<span class="slash">/06</span>`
    this.titleTarget.classList.add("out")
    this.descTarget.classList.add("out")
    clearTimeout(this.swapTimer)
    this.swapTimer = setTimeout(() => {
      this.titleTarget.textContent = node.dataset.stepTitle
      this.descTarget.textContent = node.dataset.stepDesc
      this.titleTarget.classList.remove("out")
      this.descTarget.classList.remove("out")
    }, this.reduceMotion ? 0 : 150)

    this.nodeTargets.forEach((node, idx) => {
      node.classList.toggle("reached", idx < step)
      node.classList.toggle("active", idx === step - 1)
    })
    this.ctaTarget.classList.toggle("show", step >= 5)
  }

  frame() {
    const n = this.beats.length

    // Lerp toward the target, but treat a big jump (initial load at depth,
    // Turbo restoring a scroll position) as a teleport, not a journey —
    // otherwise the Back button replays the whole conversation.
    const delta = this.targetProgress - this.displayProgress
    if (this.reduceMotion || Math.abs(delta) > 0.25) {
      this.displayProgress = this.targetProgress
      this.chatCurrent = this.chatTarget
    } else {
      this.displayProgress += delta * 0.14
    }

    const beatF = this.displayProgress * n
    const head = Math.min(n - 1, Math.floor(beatF))
    const revealed = head + 1

    let lastOn = null
    this.beats.forEach((el, i) => {
      const on = i < revealed
      el.classList.toggle("on", on)
      if (on) lastOn = el
    })

    // typing indicator while the next (bot) beat approaches; the typing bubble
    // is authored as the thread's last child, so visibility is all we manage
    const next = this.beats[revealed]
    const showTyping = next && next.dataset.role !== "user" &&
      this.displayProgress < 0.999 && (beatF - head) > 0.15
    this.typingTarget.classList.toggle("on", !!showTyping)

    // budget slider settles once its beat lands
    if (!this.budgetSet && this.budgetIndex >= 0 && this.beats[this.budgetIndex].classList.contains("on")) {
      if (this.budgetFill) this.budgetFill.style.width = "62%"
      if (this.budgetKnob) this.budgetKnob.style.left = "62%"
      this.budgetSet = true
    }

    // before/after wipe across the beat after the render lands
    if (this.render && this.afterWrap && this.wipeLine && this.renderIndex >= 0) {
      const wipe = Math.min(1, Math.max(0, beatF - this.renderIndex))
      this.afterWrap.style.clipPath = `inset(0 ${(1 - wipe) * 100}% 0 0)`
      this.wipeLine.style.left = `${wipe * 100}%`
      this.render.classList.toggle("live", wipe > 0.02 && wipe < 0.98)
    }

    // book fan opens just after its beat lands
    if (this.book && this.bookIndex >= 0) {
      this.book.classList.toggle("open",
        this.beats[this.bookIndex].classList.contains("on") && (beatF - this.bookIndex) > 0.05)
    }

    this.fillTarget.style.height = `${this.displayProgress * 100}%`
    let step = parseInt(this.beats[head].dataset.step, 10)
    if (this.displayProgress <= 0.001) step = 1
    this.setStep(step)

    // auto-scroll the chat to follow the newest content
    if (lastOn) {
      const elTop = lastOn.offsetTop
      const elHeight = lastOn.offsetHeight
      const viewHeight = this.scrollboxTarget.clientHeight
      this.chatTarget = elHeight > viewHeight * 0.8 ? elTop - 20 : elTop + elHeight - viewHeight + 22
      if (this.chatTarget < 0) this.chatTarget = 0
    }
    this.chatCurrent += (this.chatTarget - this.chatCurrent) * (this.reduceMotion ? 1 : 0.16)
    this.scrollboxTarget.scrollTop = this.chatCurrent

    this.restartTarget.classList.toggle("show", this.displayProgress > 0.985)

    this.rafId = requestAnimationFrame(() => this.frame())
  }
}
